import type { LineView, SaveLineRequest } from '../types'

/** A row in the weekly grid: one project, seven cells. */
export interface GridRow {
  /** Stable only within the editing session — the server assigns real line ids. */
  key: string
  projectId: string
  notes: string
  /** ISO date -> hours typed. Blank cells are absent, not zero. */
  hours: Record<string, number>
}

export const DAY_COUNT = 7

/**
 * Adds days to an ISO date without going through the local timezone.
 *
 * <p>Deliberately UTC end to end, and left that way by the Phase 10 sweep that moved every
 * other "today" to the browser's own zone. This is not asking what day it is: it is arithmetic
 * on a date string the server already chose, and doing it locally would shift a week's grid by
 * a day whenever the offset crosses midnight.
 */
export function addDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  // eslint-disable-next-line no-restricted-syntax -- deliberately UTC; see above
  return date.toISOString().slice(0, 10)
}

/**
 * The Monday of the week containing a date.
 *
 * Mirrors the backend, which keys one timesheet per (employee, week start). Getting this
 * wrong by a day would post Sunday's hours to the wrong sheet, and the sheet the person
 * was looking at would silently not be the one they saved.
 */
export function weekStartFor(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`)
  // getUTCDay: Sunday is 0, so Sunday is six days *after* its Monday, not one before.
  const offset = (date.getUTCDay() + 6) % 7
  return addDays(isoDate, -offset)
}

export function weekDates(weekStart: string): string[] {
  return Array.from({ length: DAY_COUNT }, (_, index) => addDays(weekStart, index))
}

/** Groups saved lines back into one row per project. */
export function toRows(lines: LineView[]): GridRow[] {
  const rows = new Map<string, GridRow>()
  for (const line of lines) {
    let row = rows.get(line.projectId)
    if (!row) {
      row = { key: line.projectId, projectId: line.projectId, notes: '', hours: {} }
      rows.set(line.projectId, row)
    }
    // Several entries on one project and day are summed: the grid has a single cell for
    // that intersection, so showing only the last one would lose hours on save.
    row.hours[line.workDate] = (row.hours[line.workDate] ?? 0) + Number(line.hours)
    if (line.notes && !row.notes) row.notes = line.notes
  }
  return [...rows.values()]
}

/** Flattens the grid back into the line list the API expects, dropping empty cells. */
export function toLines(rows: GridRow[]): SaveLineRequest[] {
  const lines: SaveLineRequest[] = []
  for (const row of rows) {
    if (!row.projectId) continue
    for (const [workDate, hours] of Object.entries(row.hours)) {
      if (!hours || hours <= 0) continue
      lines.push({
        projectId: row.projectId,
        taskId: null,
        workDate,
        hours,
        billable: null,
        notes: row.notes.trim() === '' ? null : row.notes.trim(),
      })
    }
  }
  return lines
}

export function rowTotal(row: GridRow): number {
  return Object.values(row.hours).reduce((sum, hours) => sum + (hours || 0), 0)
}

export function dayTotal(rows: GridRow[], isoDate: string): number {
  return rows.reduce((sum, row) => sum + (row.hours[isoDate] || 0), 0)
}

export function gridTotal(rows: GridRow[]): number {
  return rows.reduce((sum, row) => sum + rowTotal(row), 0)
}

/**
 * Client-side guards mirroring the server's.
 *
 * The server is the authority; these exist so someone typing 25 hours into Tuesday sees
 * it immediately instead of after losing the rest of the week to a rejected save.
 */
export function gridProblems(rows: GridRow[], dates: string[]): string[] {
  const problems: string[] = []

  for (const date of dates) {
    const total = dayTotal(rows, date)
    if (total > 24) problems.push(`${date} adds up to ${total} hours — a day has 24.`)
  }

  const duplicates = rows
    .map((row) => row.projectId)
    .filter((projectId, index, all) => projectId !== '' && all.indexOf(projectId) !== index)
  if (duplicates.length > 0) {
    problems.push('The same project appears on more than one row — put its hours on one row.')
  }

  return problems
}
